import { parseParams } from "@authentication/utils";
import { Banner } from "@components/Banner";
import { Notifications } from "@components/Notifications";
import { invenioConfig } from "@config";
import { goTo } from "@history";
import { FrontSiteRoutes } from "@routes/urls";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Link } from "react-router-dom";
import {
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Segment,
  Image,
} from "semantic-ui-react";
import { LoginWithLocalAccount } from "./LoginWithLocalAccount";
import { LoginWithOauthProviders } from "./LoginWithOauthProviders";

// Constants
const COOKIE_EXPIRY_DAYS = 1;
const COOKIE_EPOCH = "Thu, 01 Jan 1970 00:00:00 UTC";
const TAGLINE = "Learn, Reach, Connect";
const LIBRARY_DESCRIPTION =
  "The RSU Learning Resource Center: A hub of scholarly resources and innovative technologies, empowering students and faculty to pursue academic excellence.";

// Cookie utility functions
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

const setCookie = (name, value, days = COOKIE_EXPIRY_DAYS) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=${COOKIE_EPOCH}; path=/;`;
};

// Link styles
const linkStyles = {
  signUp: {
    color: "#2c5f2d",
    fontWeight: "600",
    textDecoration: "none",
    transition: "all 0.3s ease",
  },
  forgotPassword: {
    color: "#225745",
    fontWeight: "500",
    textDecoration: "none",
    transition: "color 0.3s ease",
  },
};

const linkHoverHandlers = {
  onMouseOver: (e) => {
    e.target.style.color = "#1e4d3d";
    e.target.style.textDecoration = "underline";
  },
  onMouseOut: (e) => {
    e.target.style.color = "#225745";
    e.target.style.textDecoration = "none";
  },
};

const SignUp = () => (
  <Container className="spaced signup-link">
    <p>
      Don't have an account yet?{" "}
      <Link
        to="/signup"
        className="auth-nav-with-icon"
        style={linkStyles.signUp}
      >
        Sign up here
      </Link>
    </p>
  </Container>
);

const LeftCol = ({ lastUser }) => {
  const isReturningUser = lastUser && lastUser.name;

  return (
    <Grid.Column width={8} textAlign="center" only="tablet computer">
      <Grid textAlign="center" columns={2} className="default-margin">
        <Grid.Row>
          <Grid.Column
            stretched
            width={8}
            textAlign="left"
            style={{ fontSize: "1rem", marginBottom: "2rem" }}
          >
            <Header.Subheader className="login-subheader nowrap">
              Library Information Management System
            </Header.Subheader>
            <Header
              className="login-greetings"
              style={{
                marginTop: "10rem",
                whiteSpace: "nowrap",
                fontSize: "4.5rem",
                lineHeight: "1.2",
              }}
            >
              {isReturningUser && (
                <>
                  Welcome Back, <br /> {lastUser.name}
                </>
              )}
            </Header>
            <Header.Subheader
              className="login-tagline"
              style={isReturningUser ? { marginLeft: "1rem" } : {}}
            >
              {isReturningUser ? (
                <>
                  Not {lastUser.name}? <br />
                  <span style={{ fontSize: "1rem" }}>
                    Sign in as different user.
                  </span>
                </>
              ) : (
                TAGLINE
              )}
            </Header.Subheader>
          </Grid.Column>
          <Grid.Column width={8} textAlign="left">
            <Grid.Row>
              <Grid.Column width={16}> </Grid.Column>
              <Grid.Column> </Grid.Column>
            </Grid.Row>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Grid.Column>
  );
};

const RightCol = ({
  hasError,
  errorMessage,
  errorHeader,
  user,
  lastUser,
  showLastUserLogin,
}) => (
  <Grid.Column
    mobile={16}
    tablet={8}
    computer={8}
    largeScreen={8}
    widescreen={8}
    textAlign="center"
    className="login-page"
  >
    {invenioConfig.APP.ENABLE_LOCAL_ACCOUNT_LOGIN && <SignUp />}

    {invenioConfig.APP.ENABLE_OAUTH_LOGIN &&
      Object.keys(invenioConfig.APP.OAUTH_PROVIDERS)
        .filter(
          (providerName) =>
            invenioConfig.APP.OAUTH_PROVIDERS[providerName].enabled
        )
        .map((providerName) => (
          <LoginWithOauthProviders
            key={providerName}
            providerName={providerName}
          />
        ))}

    {invenioConfig.APP.ENABLE_LOCAL_ACCOUNT_LOGIN && (
      <>
        <LoginWithLocalAccount
          hasError={hasError}
          errorHeader={errorHeader}
          errorMessage={errorMessage}
          user={user}
          lastUser={lastUser}
          showLastUserLogin={showLastUserLogin}
        />
        <Container fluid>
          <p>
            Forgot your password? <br />
            Recover{" "}
            <Link
              to="/forgot-password"
              style={linkStyles.forgotPassword}
              {...linkHoverHandlers}
            >
              here
            </Link>
            .
          </p>
        </Container>
      </>
    )}
  </Grid.Column>
);

const LoginLayout = ({
  hasError,
  errorHeader,
  errorMessage,
  backgroundImage,
  showLogo,
  lastUser,
  showLastUserLogin,
  ...props
}) => {
  return (
    <Overridable
      id="Login.layout"
      backgroundImage={backgroundImage}
      showLogo={showLogo}
      {...props}
    >
      <>
        <Notifications />
        <div className="frontsite login-page">
          <Container
            fluid
            className="auth-page blur"
            style={{
              backgroundImage: backgroundImage
                ? `url(${backgroundImage})`
                : null,
            }}
          >
            <Container className="login-container">
              <Segment className="background-transparent pb-default pt-default">
                <Container textAlign="center">
                  <Image.Group size="small">
                    <Image
                      src={`${process.env.PUBLIC_URL}/images/university-logo.png`}
                    />
                    <Image
                      src={`${process.env.PUBLIC_URL}/images/lrc-logo.png`}
                    />
                  </Image.Group>
                </Container>

                <Grid
                  stretched
                  verticalAlign="middle"
                  textAlign="center"
                  centered
                  columns={2}
                  padded
                >
                  <Grid.Row>
                    <LeftCol lastUser={lastUser} />
                    <RightCol
                      hasError={hasError}
                      errorHeader={errorHeader}
                      errorMessage={errorMessage}
                      lastUser={lastUser}
                      showLastUserLogin={showLastUserLogin}
                    />
                  </Grid.Row>
                </Grid>

                <Overridable id="Login.layout.extras" />

                <Divider />

                <Container className="login-message" fluid>
                  <p>{LIBRARY_DESCRIPTION}</p>
                  <Link className="alternative" to={FrontSiteRoutes.home}>
                    <Icon name="home" />
                    Home page
                  </Link>
                </Container>
              </Segment>
            </Container>
          </Container>
        </div>
      </>
    </Overridable>
  );
};

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorHeader: "",
      errorMessage: "",
      lastUser: null,
      showLastUserLogin: false,
    };
  }

  componentDidMount() {
    this.showNotificationIfSessionExpired();
    this.checkForLastUser();
    this.redirectIfAlreadyLoggedIn();
  }

  componentDidUpdate(prevProps) {
    const { isAnonymous, user, isLoading } = this.props;

    if (prevProps.isAnonymous && !isAnonymous && user) {
      this.saveUserToCookie(user);
    }

    if (
      prevProps.isLoading !== isLoading ||
      prevProps.isAnonymous !== isAnonymous
    ) {
      this.redirectIfAlreadyLoggedIn();
    }
  }

  checkForLastUser = () => {
    const lastUserCookie = getCookie("lastUser");
    if (lastUserCookie) {
      try {
        const lastUser = JSON.parse(decodeURIComponent(lastUserCookie));
        this.setState({
          lastUser,
          showLastUserLogin: true,
        });
      } catch (error) {
        deleteCookie("lastUser");
      }
    }
  };

  saveUserToCookie = (user) => {
    const userData = {
      name: user.profile?.full_name || user.email || user.username,
      email: user.email,
      id: user.id,
    };

    const encodedUserData = encodeURIComponent(JSON.stringify(userData));
    setCookie("lastUser", encodedUserData);
  };

  handleSignInAsDifferent = () => {
    deleteCookie("lastUser");
    this.setState({
      lastUser: null,
      showLastUserLogin: false,
    });
  };

  showNotificationIfSessionExpired = () => {
    const params = parseParams(window.location.search);
    const { hasError } = this.state;

    if ("sessionExpired" in params && !hasError) {
      this.setState({
        hasError: true,
        errorHeader: "Session Error",
        errorMessage:
          "You are either not signed in or your session has expired. Please sign in again.",
      });
    }
  };

  redirectIfAlreadyLoggedIn = () => {
    const { isLoading, isAnonymous } = this.props;
    const params = parseParams(window.location.search);

    if (!isLoading && !isAnonymous && !("sessionExpired" in params)) {
      setTimeout(() => {
        this.setState({
          hasError: false,
          errorHeader: "",
          errorMessage: "",
        });
        goTo(params.next || FrontSiteRoutes.home);
      }, 0);
    }
  };

  render() {
    const { hasError, errorHeader, errorMessage, lastUser, showLastUserLogin } =
      this.state;
    const { isLoading, isAnonymous } = this.props;

    if (!isLoading && !isAnonymous) {
      const params = parseParams(window.location.search);
      if (!("sessionExpired" in params)) {
        return null;
      }
    }

    return (
      <>
        <Overridable id="Login.extras">
          <Banner />
        </Overridable>
        <LoginLayout
          user={this.props.user}
          hasError={hasError}
          errorHeader={errorHeader}
          errorMessage={errorMessage}
          lastUser={lastUser}
          showLastUserLogin={showLastUserLogin}
          {...this.props}
        />
      </>
    );
  }
}

Login.propTypes = {
  backgroundImage: PropTypes.string,
  showLogo: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isAnonymous: PropTypes.bool.isRequired,
  user: PropTypes.object,
};

Login.defaultProps = {
  backgroundImage: null,
  showLogo: true,
  user: {},
};

LeftCol.propTypes = {
  lastUser: PropTypes.object,
};

LeftCol.defaultProps = {
  lastUser: null,
};

RightCol.propTypes = {
  hasError: PropTypes.bool,
  errorHeader: PropTypes.string,
  errorMessage: PropTypes.string,
  user: PropTypes.object,
  lastUser: PropTypes.object,
  showLastUserLogin: PropTypes.bool,
};

RightCol.defaultProps = {
  hasError: false,
  errorHeader: "",
  errorMessage: "",
  user: {},
  lastUser: null,
  showLastUserLogin: false,
};

LoginLayout.propTypes = {
  ...Login.propTypes,
  lastUser: PropTypes.object,
  showLastUserLogin: PropTypes.bool,
};

LoginLayout.defaultProps = {
  ...Login.defaultProps,
  lastUser: null,
  showLastUserLogin: false,
};

export default Overridable.component("LoginPage", Login);
